import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { toast } from "sonner";

const POSITIONS = ["Målvakt", "Høyre back", "Venstre back", "Senter", "Høyre ving", "Venstre ving"];

export default function PlayerForm({ teams, matches, onCreated, defaultTeamId, defaultMatchId, trigger }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    team_id: defaultTeamId || "",
    match_id: defaultMatchId || "",
    full_name: "",
    date_of_birth: "",
    jersey_number: "",
    position: "",
    email: "",
    nihf_license: "",
    nihf_club: "",
    confirms_no_other_league: false,
    lagleder_license_confirmed: false,
  });

  React.useEffect(() => {
    setForm((f) => ({ ...f, team_id: defaultTeamId || f.team_id, match_id: defaultMatchId || f.match_id }));
  }, [defaultTeamId, defaultMatchId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.lagleder_license_confirmed) {
      toast.error("Du må bekrefte at spilleren oppfyller alle krav");
      return;
    }
    if (!form.team_id || !form.match_id || !form.position) {
      toast.error("Fyll inn lag, kamp og posisjon");
      return;
    }
    if (!form.date_of_birth) {
      toast.error("Fødselsdato kreves");
      return;
    }
    try {
      setSubmitting(true);
      // Single license confirmation also covers the 4 krav (license, no other series, max OB, over 16)
      const payload = {
        ...form,
        jersey_number: Number(form.jersey_number),
        confirms_no_other_league: true,
      };
      const res = await api.post("/players", payload);
      toast.success("Spiller meldt på – venter på godkjenning");
      setOpen(false);
      setForm({ team_id: defaultTeamId || "", match_id: defaultMatchId || "", full_name: "", date_of_birth: "", jersey_number: "", position: "", email: "", nihf_license: "", nihf_club: "", confirms_no_other_league: false, lagleder_license_confirmed: false });
      onCreated && onCreated(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke melde på spiller");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || <Button data-testid="open-player-form" className="bg-black text-white hover:bg-neutral-800 rounded-sm font-heading uppercase tracking-widest">+ Meld på spiller</Button>}
      </DialogTrigger>
      <DialogContent className="max-w-xl rounded-sm max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-tight text-2xl">Meld på spiller</DialogTitle>
        </DialogHeader>

        <div className="bg-neutral-50 border border-neutral-200 p-3 rounded-sm text-xs">
          <div className="font-heading uppercase tracking-widest text-neutral-500 mb-1">Spilleren må oppfylle alle 3 krav</div>
          <ol className="grid grid-cols-1 gap-1 text-neutral-700">
            <li>1. Gyldig rekreasjonshockey-lisens hos NIHF for lagets klubb</li>
            <li>2. Har ikke spilt høyere enn Old Boys (OB)</li>
            <li>3. Er over 16 år</li>
          </ol>
          <div className="mt-2 text-[10px] text-neutral-500 italic">NB: NIHF-lisens er eksklusiv — én lisens om gangen utelukker dobbeltspill i andre serier.</div>
        </div>

        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-widest">Lag</Label>
            <Select value={form.team_id} onValueChange={(v) => set("team_id", v)}>
              <SelectTrigger data-testid="select-team" className="rounded-sm"><SelectValue placeholder="Velg lag" /></SelectTrigger>
              <SelectContent>
                {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-widest">Kamp & dato</Label>
            <Select value={form.match_id} onValueChange={(v) => set("match_id", v)}>
              <SelectTrigger data-testid="select-match" className="rounded-sm"><SelectValue placeholder="Velg kamp" /></SelectTrigger>
              <SelectContent>
                {matches.map((m) => <SelectItem key={m.id} value={m.id}>{m.title} — {new Date(m.match_date).toLocaleString("nb-NO", { dateStyle: "short", timeStyle: "short" })}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-widest">Navn</Label>
            <Input data-testid="input-name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} required className="rounded-sm" />
          </div>

          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-widest">Fødselsdato</Label>
            <Input data-testid="input-dob" type="date" value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} required className="rounded-sm" />
            <p className="text-xs text-neutral-500 mt-1">Brukes for å verifisere spilleren og skille navnetreff.</p>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest">Nummer</Label>
            <Input data-testid="input-jersey" type="number" min="0" max="99" value={form.jersey_number} onChange={(e) => set("jersey_number", e.target.value)} required className="rounded-sm" />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest">Posisjon</Label>
            <Select value={form.position} onValueChange={(v) => set("position", v)}>
              <SelectTrigger data-testid="select-position" className="rounded-sm"><SelectValue placeholder="Velg posisjon" /></SelectTrigger>
              <SelectContent>
                {POSITIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-widest">E-post</Label>
            <Input data-testid="input-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required className="rounded-sm" />
          </div>

          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-widest">NIHF Lisensnummer</Label>
            <Input data-testid="input-license" value={form.nihf_license} onChange={(e) => set("nihf_license", e.target.value)} required className="rounded-sm" />
            <p className="text-xs text-neutral-500 mt-1">Minst 5 tegn. Admin kan kryssjekke på <a className="underline" target="_blank" rel="noopener noreferrer" href={`https://www.eliteprospects.com/search/player?q=${encodeURIComponent(form.full_name || "")}`}>Elite Prospects</a>.</p>
          </div>

          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-widest">Klubb hos NIHF</Label>
            <Input data-testid="input-nihf-club" value={form.nihf_club} onChange={(e) => set("nihf_club", e.target.value)} placeholder="F.eks. Strindheim Ishockeyklubb" className="rounded-sm" />
            <p className="text-xs text-neutral-500 mt-1">Klubb spilleren har gyldig rekreasjonshockey-lisens for.</p>
          </div>

          <div className="sm:col-span-2 flex items-start gap-3 p-3 border-2 border-emerald-300 rounded-sm bg-emerald-50">
            <Checkbox data-testid="confirm-license" id="confirm-license" checked={form.lagleder_license_confirmed} onCheckedChange={(v) => set("lagleder_license_confirmed", !!v)} />
            <label htmlFor="confirm-license" className="text-sm leading-snug">
              <strong>Lagleders bekreftelse:</strong> Jeg har sjekket i <strong>NIHF SportsAdmin</strong> at spilleren har gyldig <strong>rekreasjonshockey-lisens</strong> for lagets klubb. Spilleren er over 16 år og har ikke spilt høyere enn Old Boys (OB). Jeg er ansvarlig for at denne bekreftelsen er korrekt.
            </label>
          </div>

          <DialogFooter className="sm:col-span-2 mt-2">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} className="rounded-sm">Avbryt</Button>
            <Button data-testid="submit-player" type="submit" disabled={submitting} className="bg-black text-white hover:bg-neutral-800 rounded-sm font-heading uppercase tracking-widest">
              {submitting ? "Lagrer…" : "Meld på"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
